/**
 * SMS Feature — Raw API functions
 */
import { apiClient } from '@/services/api/client'
import { API_ENDPOINTS } from '@/services/api/endpoints'
import type {
  PaginatedResponse,
  SmsMessage,
  SmsTemplate,
  SmsCampaign,
  CampaignMessage,
  SmsCredit,
  SmsListParams,
  CampaignListParams,
  CampaignMessageParams,
  CreateTemplatePayload,
  UpdateTemplatePayload,
  CreateCampaignPayload,
  RecipientFilters,
  RecipientPreview,
} from '@/types'

// ── Messages ─────────────────────────────────────────────────────────────────

export async function fetchSmsMessages(
  params: SmsListParams,
): Promise<PaginatedResponse<SmsMessage>> {
  const res = await apiClient.get<PaginatedResponse<SmsMessage>>(
    API_ENDPOINTS.SMS.MESSAGES,
    { params },
  )
  return res.data
}

export async function fetchSmsMessage(id: string): Promise<SmsMessage> {
  const res = await apiClient.get<{data: SmsMessage}>(API_ENDPOINTS.SMS.MESSAGE_DETAIL(id))
  return res.data.data
}

// ── Templates ─────────────────────────────────────────────────────────────────

export async function fetchSmsTemplates(): Promise<SmsTemplate[]> {
  const res = await apiClient.get<{data: SmsTemplate[]}>(API_ENDPOINTS.SMS.TEMPLATES)
  return res.data.data
}

export async function createSmsTemplate(
  payload: CreateTemplatePayload,
): Promise<SmsTemplate> {
  const res = await apiClient.post<{data: SmsTemplate}>(API_ENDPOINTS.SMS.TEMPLATES, payload)
  return res.data.data
}

export async function updateSmsTemplate(
  id: string,
  payload: UpdateTemplatePayload,
): Promise<SmsTemplate> {
  const res = await apiClient.patch<{data: SmsTemplate}>(
    API_ENDPOINTS.SMS.TEMPLATE_DETAIL(id),
    payload,
  )
  return res.data.data
}

export async function deleteSmsTemplate(id: string): Promise<void> {
  await apiClient.delete(API_ENDPOINTS.SMS.TEMPLATE_DETAIL(id))
}

// ── Campaigns ─────────────────────────────────────────────────────────────────

export async function fetchCampaigns(
  params: CampaignListParams,
): Promise<PaginatedResponse<SmsCampaign>> {
  const res = await apiClient.get<PaginatedResponse<SmsCampaign>>(
    API_ENDPOINTS.SMS.CAMPAIGNS,
    { params },
  )
  return res.data
}

export async function fetchCampaign(id: string): Promise<SmsCampaign> {
  const res = await apiClient.get<{data: SmsCampaign}>(API_ENDPOINTS.SMS.CAMPAIGN_DETAIL(id))
  return res.data.data
}

export async function createCampaign(
  payload: CreateCampaignPayload,
): Promise<SmsCampaign> {
  const res = await apiClient.post<{data: SmsCampaign}>(API_ENDPOINTS.SMS.CAMPAIGNS, payload)
  return res.data.data
}

export async function sendCampaign(id: string): Promise<SmsCampaign> {
  const res = await apiClient.post<{data: SmsCampaign}>(API_ENDPOINTS.SMS.CAMPAIGN_SEND(id))
  return res.data.data
}

export async function cancelCampaign(id: string): Promise<SmsCampaign> {
  const res = await apiClient.post<{data: SmsCampaign}>(API_ENDPOINTS.SMS.CAMPAIGN_CANCEL(id))
  return res.data.data
}

export async function fetchCampaignMessages(
  id: string,
  params: CampaignMessageParams,
): Promise<PaginatedResponse<CampaignMessage>> {
  const res = await apiClient.get<PaginatedResponse<CampaignMessage>>(
    API_ENDPOINTS.SMS.CAMPAIGN_MESSAGES(id),
    { params },
  )
  return res.data
}

export async function previewRecipients(
  filters: RecipientFilters,
): Promise<RecipientPreview> {
  const res = await apiClient.post<{data: RecipientPreview}>(
    API_ENDPOINTS.SMS.CAMPAIGN_PREVIEW,
    filters,
  )
  return res.data.data
}

// ── Credits ───────────────────────────────────────────────────────────────────

export async function fetchSmsCredits(): Promise<SmsCredit> {
  const res = await apiClient.get<{data: SmsCredit}>(API_ENDPOINTS.SMS.CREDITS)
  return res.data.data
}

export interface TopUpPayload {
  amount: number
  reference: string
  description?: string
}

export async function topUpCredits(payload: TopUpPayload): Promise<SmsCredit> {
  const res = await apiClient.post<{data: SmsCredit}>(API_ENDPOINTS.SMS.CREDIT_TOP_UP, payload)
  return res.data.data
}

export async function fetchCreditTransactions(params?: {
  page?: number
  page_size?: number
}): Promise<import('@/types').PaginatedResponse<import('@/types').SmsCreditTransaction>> {
  const res = await apiClient.get(API_ENDPOINTS.SMS.CREDIT_TRANSACTIONS, { params })
  return res.data.data
}

export async function fetchCampaignAnalytics(id: string): Promise<import('@/types').CampaignAnalytics> {
  const res = await apiClient.get<{ data: import('@/types').CampaignAnalytics }>(
    API_ENDPOINTS.SMS.CAMPAIGN_ANALYTICS(id),
  )
  return res.data.data
}
